import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { formatAppDate } from '../../utils/dateFormat';
import PageHeader from '../../components/PageHeader';
import { useAuth } from '../../contexts/AuthContext';

type CabinetPersona = 'advertiser' | 'publisher' | 'agency';

const PERSONA_KEY = 'trendy_cabinet_persona';

const personaLabel: Record<CabinetPersona, string> = {
  advertiser: 'Рекламодатель',
  publisher: 'Блогер / издатель',
  agency: 'Агентство',
};

function loadPersona(): CabinetPersona | null {
  try {
    const v = localStorage.getItem(PERSONA_KEY);
    if (v === 'advertiser' || v === 'publisher' || v === 'agency') return v;
  } catch {
    // ignore
  }
  return null;
}

const ClientHomePage: React.FC = () => {
  const { user, isClientAdmin } = useAuth();
  const tariffName = user?.tariff === 'BUSINESS' ? 'Бизнес' : 'Старт';
  const subscriptionActive = user?.subscriptionActive !== false;
  const isYandex = user?.ordProvider === 'YANDEX';
  const isStart = user?.tariff === 'START';

  const [persona, setPersona] = useState<CabinetPersona | null>(() => loadPersona());

  const choosePersona = (_: React.MouseEvent<HTMLElement>, value: CabinetPersona | null) => {
    if (!value) return;
    setPersona(value);
    try {
      localStorage.setItem(PERSONA_KEY, value);
    } catch {
      // ignore
    }
  };

  const steps = useMemo(() => {
    if (persona === 'advertiser') {
      return [
        { to: '/cabinet/persons', text: 'Заведите издателя/блогера (роль РР) и при необходимости укажите ссылки на каналы' },
        { to: '/cabinet/contracts', text: 'Создайте договор: вы — заказчик, исполнитель — блогер' },
        { to: '/cabinet/creatives', text: 'Создайте сторонний креатив, выберите договор «номер — блогер», отправьте в ОРД' },
        { to: '/cabinet/creatives', text: 'После публикации привяжите ссылки на ролики — дальше сбор и сдача статистики' },
      ];
    }
    if (persona === 'publisher') {
      return [
        {
          to: '/cabinet/persons',
          text: isStart
            ? 'Заведите рекламодателя (роль РД) — без ссылок на каналы'
            : 'Заведите рекламодателя (роль РД). Свои каналы — в «Площадки» или при создании контрагента-издателя',
        },
        { to: '/cabinet/platforms', text: 'Проверьте площадки своих каналов (YouTube / VK…) — для парсинга erid' },
        { to: '/cabinet/contracts', text: 'Договор: заказчик — рекламодатель, вы — исполнитель (или наоборот по факту)' },
        {
          to: '/cabinet/creatives',
          text: user?.allowSelfAd
            ? 'Самореклама — без договора; сторонняя — с договором с РД. После выхода ролика — ссылки'
            : 'Создайте сторонний креатив по договору с РД, отправьте в ОРД, привяжите ссылки',
        },
      ];
    }
    if (persona === 'agency') {
      return [
        { to: '/cabinet/persons', text: 'Контрагенты: рекламодатели (без ссылок) и блогеры (со ссылками на каналы → площадки)' },
        {
          to: '/cabinet/contracts',
          text: 'Посреднический договор: заказчик = РД, исполнитель = блогер. Один номер можно на нескольких блогеров — разные записи',
        },
        { to: '/cabinet/creatives', text: 'Креатив: выберите договор «номер — блогер» (издатель подтянется сам)' },
        {
          to: '/cabinet/settings',
          text: isClientAdmin
            ? 'В Настройках или на Контрагентах выдайте доступ блогеру — он увидит только свои креативы'
            : 'После публикации — ссылки; в конце месяца акты и статистика по erid',
        },
      ];
    }
    return [
      { to: '/cabinet/persons', text: 'Добавьте контрагента (рекламодателя или издателя)' },
      { to: '/cabinet/contracts', text: 'Создайте договор' },
      { to: '/cabinet/creatives', text: 'Создайте креатив и отправьте в ОРД' },
      { to: '/cabinet/creatives', text: 'После публикации привяжите ссылки на ролики' },
    ];
  }, [persona, isStart, user?.allowSelfAd, isClientAdmin]);

  const tip = useMemo(() => {
    if (persona === 'advertiser') {
      return 'Вы заказываете размещение: договор с блогером, креатив на этот договор, статистика собирается с его каналов.';
    }
    if (persona === 'publisher') {
      return 'Вы размещаете рекламу у себя: договоры с рекламодателями, свои площадки, самореклама — если включена в кабинете.';
    }
    if (persona === 'agency') {
      return 'Вы посредник: в одном посредническом договоре заказчик — РД, исполнитель — блогер. В списках — «номер — блогер».';
    }
    return null;
  }, [persona]);

  return (
    <Box>
      <PageHeader
        title="Главная"
        description="Добро пожаловать в кабинет ТрендиОРД"
      />
      <Stack spacing={2}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            {user?.ordClientName || 'Ваш кабинет'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Тариф «{tariffName}»
            {user?.ordProvider === 'YANDEX' ? ' · Яндекс ОРД' : user?.ordProvider === 'OZON' ? ' · Ozon ОРД' : user?.ordProvider === 'VK' ? ' · VK ОРД' : ''}
            . Создавайте контрагентов, договоры и креативы — система поможет сдать статистику в ОРД.
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              Подписка: {subscriptionActive ? 'активна' : 'неактивна'}
            </Typography>
            {user?.subscriptionPaidUntil && (
              <Typography variant="body2">
                Оплачено до: {formatAppDate(user.subscriptionPaidUntil)}
              </Typography>
            )}
          </Stack>
        </Paper>
        {!subscriptionActive && (
          <Alert severity="warning">
            Срок подписки истёк. Обратитесь к администратору Тренди для продления.
          </Alert>
        )}

        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Кто вы в этом кабинете?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Выберите роль — покажем короткий чеклист под ваш сценарий. Можно сменить в любой момент.
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            color="primary"
            value={persona}
            onChange={choosePersona}
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                py: 1.25,
                fontWeight: 600,
              },
            }}
          >
            <ToggleButton value="advertiser">{personaLabel.advertiser}</ToggleButton>
            <ToggleButton value="publisher">{personaLabel.publisher}</ToggleButton>
            <ToggleButton value="agency">{personaLabel.agency}</ToggleButton>
          </ToggleButtonGroup>
          {tip && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {tip}
            </Typography>
          )}
        </Paper>

        {isYandex && (
          <Paper sx={{ p: 2, border: (t) => `1px solid ${t.palette.warning.light}` }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Яндекс ОРД: перенос старых данных *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              У Яндекс ОРД нет API-выгрузки списков (контрагенты, договоры, креативы, площадки, акты, история показов).
              Старые объекты переносятся <strong>вручную через Excel</strong> на соответствующих страницах.
              Всё, что создаёте дальше в ТрендиОРД, уходит в Яндекс автоматически — полный функционал без ручной выгрузки.
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
              Порядок импорта (один раз при подключении)
            </Typography>
            <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0, mb: 1.5 }}>
              <li>
                <MuiLink component={RouterLink} to="/cabinet/persons">Контрагенты</MuiLink>
                {' '}— шаблон Excel → object_id, название, ИНН, адрес
              </li>
              <li>
                <MuiLink component={RouterLink} to="/cabinet/contracts">Договоры</MuiLink>
                {' '}— стороны по external_id уже загруженных контрагентов
              </li>
              <li>
                <MuiLink component={RouterLink} to="/cabinet/creatives">Креативы</MuiLink>
                {' '}— external_id, erid, договор; затем привяжите ссылки на ролики
              </li>
              <li>
                <MuiLink component={RouterLink} to="/cabinet/platforms">Площадки</MuiLink>
                {' '}— object_id и URL каналов для автосбора
              </li>
              <li>
                <MuiLink component={RouterLink} to="/cabinet/invoices">Акты</MuiLink>
                {' '}— номера уже выставленных актов (если нужны в кабинете)
              </li>
              <li>
                <MuiLink component={RouterLink} to="/cabinet/settings">Настройки</MuiLink>
                {' '}— Excel «уже сданные показы» (erid | сумма), чтобы не сдать дубли в ЕРИР
              </li>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              * Автоматической выгрузки старых данных из Яндекс ОРД нет — только ручная. Для новых креативов,
              актов и площадок, созданных через ТрендиОРД, доступен полный функционал.
            </Typography>
          </Paper>
        )}

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {persona ? `С чего начать · ${personaLabel[persona]}` : 'С чего начать'}
          </Typography>
          {!persona && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Выберите роль выше — шаги станут точнее. Пока общий минимум:
            </Typography>
          )}
          <Stack component="ol" spacing={1} sx={{ pl: 2.5, m: 0 }}>
            {steps.map((s, i) => (
              <Typography key={i} component="li" variant="body2">
                <MuiLink component={RouterLink} to={s.to}>{s.text}</MuiLink>
              </Typography>
            ))}
          </Stack>
          {persona && (
            <Button
              size="small"
              sx={{ mt: 1.5 }}
              onClick={() => {
                setPersona(null);
                try {
                  localStorage.removeItem(PERSONA_KEY);
                } catch {
                  // ignore
                }
              }}
            >
              Сбросить выбор роли
            </Button>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ClientHomePage;
